import Vue from 'vue'

function getObjectPath(object, path, defaultResponse) {
  if (object) {
    let bits = path.split('.')
    for (let key of bits) {
      try {
        if (Object.keys(object).indexOf(key) !== -1) {
          object = object[key]
        } else {
          return defaultResponse
        }
      } catch (e) {
        if (e instanceof TypeError) {
          return defaultResponse
        }
        throw e
      }
    }
    return object
  }
  return defaultResponse
}

export default {
  namespaced: true,
  state: {
    me: {},
    lookups: {
      locations: {},
      practitioners: {},
      spaces: {},
      businesses: []
    }
  },
  mutations: {
    setMe(state, data) {
      Vue.set(state, 'me', data)
    },
    setLookup(state, { key, values }) {
      Vue.set(state.lookups, key, values)
    },
    setLookups(state, data) {
      Vue.set(state, 'lookups', data)
    }
  },
  getters: {
    me(state) {
      return state.me
    },
    meId(state, getters) {
      return getObjectPath(getters.me, 'user.id')
    },
    meFirstName(state, getters) {
      return getObjectPath(getters.me, 'user.first_name')
    },
    meFullname(state, getters) {
      let str = ''
      let firstName = getObjectPath(getters.me, 'user.first_name')
      let lastName = getObjectPath(getters.me, 'user.last_name')
      if (firstName) { str = firstName }
      if (lastName) { str += ' ' + lastName }
      return str
    },
    meProfile(state, getters) {
      if (getters.meId) {
        let p = getters.getPractitionerById(getters.meId)
        if (p && p.profile_picture) {
          return p.profile_picture
        }
        return null
      }
    },
    meInitials(state, getters) {
      if (getters.meFullname) {
        let items = getters.meFullname.split(' ')
        if (items && items.length > 0) {
          return items.slice(0, 2).map((word) => { return word[0] }).join('')
        }
      }
      return '??'
    },
    lookups(state) {
      return state.lookups
    },
    businesses(state) {
      if (state.lookups && state.lookups.businesses) {
        return state.lookups.businesses
      }
      return []
    },
    spaces(state) {
      return state.lookups.spaces
    },
    spaceIds(state, getters) {
      let spaces = getters.spaces
      if (spaces) { return Object.keys(spaces) }
      return []
    },
    spacesList(state, getters) {
      let spaces = getters.spaces
      if (spaces) { return Object.values(spaces) }
      return []
    },
    spacesIOwnList(state, getters) {
      let spaces = getters.spacesIOwn
      if (spaces) { return Object.values(spaces) }
      return []
    },
    locationsList(state, getters) {
      let locations = getters.lookups.locations
      if (locations) { return Object.values(locations) }
      return []
    },
    spacesIOwn(state, getters) {
      // let meId = getters.meId
      return getters.spacesList.filter((space) => {
        return (space.role === 'Owner' && space.archived === false)
      })
    },
    getSpacesByPractitionerId: (state, getters) => (practitionerId) => {
      return getters.spacesList.filter((space) => {
        if (space && space.members) {
          return space.members.indexOf(practitionerId) >= 0
        }
        return false
      })
    },
    productsList(state, getters) {
      let products = getters.lookups.products
      if (products) { return Object.values(products) }
      return []
    },
    getLocations(state, getters) {
      let spaces = getters.spaceIds
      let locs = []
      spaces.forEach((spaceId) => {
        let li = getters.getLocationsBySpaceId(spaceId)
        if (li) { locs = locs.concat(li) }
      })
      return locs
    },
    getLocationsBySpaceId: (state, getters) => (spaceId) => {
      let space = getters.getSpaceById(spaceId)
      if (space) {
        return space.locations
      }
      return []
    },
    getLocationBySpaceAndId: (state, getters) => (spaceId, locationId) => {
      let locs = getters.getLocationsBySpaceId(spaceId)
      return locs.find((loc) => {
        return String(loc.id) === String(locationId)
      })
    },
    practitionersList(state, getters) {
      if (getters.lookups && getters.lookups.practitioners) {
        return Object.values(getters.lookups.practitioners)
      }
      return []
    },
    getPractitionersById: (state, getters) => (ids) => {
      return ids.map((id) => {
        return getters.getPractitionerById(id)
      })
    },
    getPractitionerById: (state, getters) => (id) => {
      let lookups = getters.lookups
      if (lookups && lookups.practitioners) {
        return lookups.practitioners[id]
      }
      return {}
    },
    // alias for getPractitionersInSpace
    getMembers: (state, getters) => (spaceId) => {
      if (!spaceId) { return [] }
      return getters.getPractitionersInSpace(spaceId)
    },
    getPractitionersInSpace: (state, getters) => (spaceId) => {
      if (!spaceId) { return [] }
      let space = getters.getSpaceById(spaceId)
      if (space && space.id) {
        let members = space.members
        members = members.map((memberId) => {
          return getters.getPractitionerById(memberId)
        })
        return members.filter((member) => {
          if (member) { return member }
        })
      }
      return []
      // return getters.practitionersList.filter((p) => {
      //   console.log(p.space, spaceId)
      //   return String(p.space) === String(spaceId)
      // })
    },
    getSpaceById: (state, getters) => (id) => {
      let lookups = getters.lookups
      if (lookups && lookups.spaces) {
        return lookups.spaces[id]
      }
      return {}
    },
    getProducts: (state, getters) => ({ spaceId, practitionerId, includeArchived }) => {
      // console.log(spaceId, practitionerId)
      return getters.productsList.filter((p) => {
        let inSpace = true
        let inPractitioner = true
        if (!includeArchived && p.archived === true) {
          return false
        }
        if (spaceId) {
          inSpace = (String(spaceId) === String(p.space))
        }
        if (practitionerId) {
          inPractitioner = (String(practitionerId) === String(p.owner))
        }
        // console.log(inSpace, inPractitioner, spaceId, practitionerId)
        if (inSpace && inPractitioner) {
          return p
        }
      })
    },
    getProductById: (state, getters) => (id) => {
      let lookups = getters.lookups
      if (lookups && lookups.products) {
        return lookups.products[id]
      }
      return {}
    }
  }
}
